import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import { useNotify } from '@routes-plan/ui';
import { Office } from '../../../models/office';
import { useT } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';
import AssignChecklist from '../shared/AssignChecklist';

interface AlertProps {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
}

/** Sedes asignadas a un usuario. */
const UserOffices: React.FC<AlertProps> = () => {
  const t = useT();
  const notify = useNotify();
  const navigate = useNavigate();
  const { id } = useParams();
  const [selectedOffice, setSelectedOffice] = useState<number[]>([]);
  const [unselectedOffice, setUnselectedOffice] = useState<number[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  setPageTitle('users.assignOfficesTitle');
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [{ data: all }, { data: mine }] = await Promise.all([axios.get('offices'), axios.get(`users/${id}/offices`)]);
        setOffices(Array.isArray(all?.data) ? all.data : []);
        setSelectedOffice((Array.isArray(mine?.users) ? mine.users : []).map((uo: { officeId: number }) => uo.officeId));
      } catch (err) {
        notify.error(t('common.loadError'));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const toggle = (officeId: number, checked: boolean) => {
    setSelectedOffice((prev) => (checked ? [...prev, officeId] : prev.filter((o) => o !== officeId)));
    setUnselectedOffice((prev) => (checked ? prev.filter((o) => o !== officeId) : prev.includes(officeId) ? prev : [...prev, officeId]));
  };

  const toggleAll = (checked: boolean) => {
    setSelectedOffice(checked ? offices.map((o) => o.id) : []);
    setUnselectedOffice(checked ? [] : offices.map((o) => o.id));
  };

  const submit = async () => {
    setSaving(true);
    try {
      await axios.put(`/users/${id}/offices`, {
        officeId: selectedOffice,
        setUnselectedOffice: unselectedOffice,
        userId: id,
      });
      notify.success(t('users.assignmentSavedOk'));
      navigate('/admin/users');
    } catch (err) {
      notify.error(t('common.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AssignChecklist
      tourPrefix="user-offices"
      title={t('users.assignOfficesTitle')}
      description={t('users.assignOfficesHint')}
      breadcrumbs={[{ label: t('users.title'), href: '#/admin/users' }, { label: t('users.assignOfficesTitle') }]}
      emptyTitle={t('users.noOfficesAvailable')}
      emptyIcon={<StorefrontOutlinedIcon />}
      items={offices.map((o) => ({ id: o.id, name: o.name }))}
      selected={selectedOffice}
      onToggle={toggle}
      onToggleAll={toggleAll}
      loading={loading}
      cancelTo="/admin/users"
      onSave={submit}
      saving={saving}
    />
  );
};

export default UserOffices;
